import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";

interface OptionFormData {
  optionName: string;
  durationModifier: number;
  isDefault: boolean;
}

interface GroupFormData {
  name: string;
  selectionType: "SINGLE" | "MULTIPLE";
  minSelections: number;
  maxSelections: number | undefined;
  options: OptionFormData[];
}

interface TemplateFormData {
  name: string;
  storefrontDisplayMode: "TABLE" | "REGULAR";
  groups: GroupFormData[];
}

interface CreateTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (input: {
    name: Record<string, string>;
    storefrontDisplayMode: "TABLE" | "REGULAR";
    groups: {
      name: Record<string, string>;
      selectionType: string;
      minSelections: number;
      maxSelections?: number;
      options: {
        optionName: Record<string, string>;
        durationModifier: number;
        isDefault: boolean;
      }[];
    }[];
  }) => Promise<void>;
  creating: boolean;
  language: string;
}

const createEmptyOption = (): OptionFormData => ({
  optionName: "",
  durationModifier: 0,
  isDefault: false,
});

const createEmptyGroup = (): GroupFormData => ({
  name: "",
  selectionType: "SINGLE",
  minSelections: 0,
  maxSelections: undefined,
  options: [createEmptyOption()],
});

const createInitialFormData = (): TemplateFormData => ({
  name: "",
  storefrontDisplayMode: "REGULAR",
  groups: [createEmptyGroup()],
});

export function CreateTemplateDialog({
  open,
  onClose,
  onCreate,
  creating,
  language,
}: CreateTemplateDialogProps) {
  const { t } = useTranslation("settings");
  const [form, setForm] = useState<TemplateFormData>(createInitialFormData);

  const handleClose = () => {
    setForm(createInitialFormData());
    onClose();
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || form.groups.length === 0) return;

    await onCreate({
      name: { [language]: form.name.trim() },
      storefrontDisplayMode: form.storefrontDisplayMode,
      groups: form.groups.map((group) => ({
        name: { [language]: group.name.trim() },
        selectionType: group.selectionType,
        minSelections: group.minSelections,
        maxSelections: group.maxSelections,
        options: group.options.map((opt) => ({
          optionName: { [language]: opt.optionName.trim() },
          durationModifier: opt.durationModifier,
          isDefault: opt.isDefault,
        })),
      })),
    });

    handleClose();
  };

  const updateGroup = (groupIdx: number, patch: Partial<GroupFormData>) => {
    setForm((prev) => ({
      ...prev,
      groups: prev.groups.map((g, i) => (i === groupIdx ? { ...g, ...patch } : g)),
    }));
  };

  const removeGroup = (groupIdx: number) => {
    setForm((prev) => ({
      ...prev,
      groups: prev.groups.filter((_, i) => i !== groupIdx),
    }));
  };

  const addGroup = () => {
    setForm((prev) => ({ ...prev, groups: [...prev.groups, createEmptyGroup()] }));
  };

  const updateOption = (groupIdx: number, optIdx: number, patch: Partial<OptionFormData>) => {
    setForm((prev) => ({
      ...prev,
      groups: prev.groups.map((g, gi) =>
        gi === groupIdx
          ? { ...g, options: g.options.map((o, oi) => (oi === optIdx ? { ...o, ...patch } : o)) }
          : g
      ),
    }));
  };

  const removeOption = (groupIdx: number, optIdx: number) => {
    setForm((prev) => ({
      ...prev,
      groups: prev.groups.map((g, gi) =>
        gi === groupIdx ? { ...g, options: g.options.filter((_, oi) => oi !== optIdx) } : g
      ),
    }));
  };

  const addOption = (groupIdx: number) => {
    setForm((prev) => ({
      ...prev,
      groups: prev.groups.map((g, gi) =>
        gi === groupIdx ? { ...g, options: [...g.options, createEmptyOption()] } : g
      ),
    }));
  };

  const isValid =
    form.name.trim().length > 0 &&
    form.groups.length > 0 &&
    form.groups.every(
      (g) => g.name.trim().length > 0 && g.options.every((o) => o.optionName.trim().length > 0)
    );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("serviceTemplates.form.createTitle")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* Template Name */}
          <div className="space-y-2">
            <Label htmlFor="template-name">{t("serviceTemplates.form.templateName")}</Label>
            <Input
              id="template-name"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder={t("serviceTemplates.form.templateName")}
            />
          </div>

          {/* Display Mode */}
          <div className="space-y-2">
            <Label>{t("serviceTemplates.form.displayMode")}</Label>
            <Select
              value={form.storefrontDisplayMode}
              onValueChange={(value: "TABLE" | "REGULAR") =>
                setForm((prev) => ({ ...prev, storefrontDisplayMode: value }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="REGULAR">
                  {t("serviceTemplates.form.displayModeRegular")}
                </SelectItem>
                <SelectItem value="TABLE">{t("serviceTemplates.form.displayModeTable")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Groups */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>{t("serviceTemplates.form.groups")}</Label>
              <Button type="button" variant="outline" size="sm" onClick={addGroup}>
                <Plus className="h-4 w-4 mr-1" />
                {t("serviceTemplates.form.addGroup")}
              </Button>
            </div>

            {form.groups.length === 0 && (
              <p className="text-sm text-muted-foreground">{t("serviceTemplates.form.noGroups")}</p>
            )}

            {form.groups.map((group, gi) => (
              <div key={gi} className="border rounded-md p-4 space-y-4">
                <div className="flex items-start gap-2">
                  <div className="flex-1 space-y-3">
                    {/* Group name */}
                    <div className="space-y-1">
                      <Label className="text-xs">{t("serviceTemplates.form.groupName")}</Label>
                      <Input
                        value={group.name}
                        onChange={(e) => updateGroup(gi, { name: e.target.value })}
                        placeholder={t("serviceTemplates.form.groupName")}
                        className="h-8"
                      />
                    </div>

                    {/* Selection type + min/max */}
                    <div className="grid grid-cols-3 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs">
                          {t("serviceTemplates.form.selectionType")}
                        </Label>
                        <Select
                          value={group.selectionType}
                          onValueChange={(v: "SINGLE" | "MULTIPLE") =>
                            updateGroup(gi, { selectionType: v })
                          }
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue>
                              {group.selectionType === "MULTIPLE"
                                ? t("serviceTemplates.form.multiple")
                                : t("serviceTemplates.form.single")}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="SINGLE">
                              {t("serviceTemplates.form.single")}
                            </SelectItem>
                            <SelectItem value="MULTIPLE">
                              {t("serviceTemplates.form.multiple")}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">
                          {t("serviceTemplates.form.minSelections")}
                        </Label>
                        <Input
                          type="number"
                          min={0}
                          value={group.minSelections}
                          onChange={(e) =>
                            updateGroup(gi, { minSelections: parseInt(e.target.value, 10) || 0 })
                          }
                          className="h-8"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">
                          {t("serviceTemplates.form.maxSelections")}
                        </Label>
                        <Input
                          type="number"
                          min={0}
                          value={group.maxSelections ?? ""}
                          onChange={(e) =>
                            updateGroup(gi, {
                              maxSelections: e.target.value
                                ? parseInt(e.target.value, 10)
                                : undefined,
                            })
                          }
                          placeholder="∞"
                          className="h-8"
                        />
                      </div>
                    </div>

                    {/* Options */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs">{t("serviceTemplates.form.options")}</Label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs"
                          onClick={() => addOption(gi)}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          {t("serviceTemplates.form.addOption")}
                        </Button>
                      </div>

                      {group.options.map((opt, oi) => (
                        <div key={oi} className="flex items-center gap-2">
                          <Input
                            value={opt.optionName}
                            onChange={(e) => updateOption(gi, oi, { optionName: e.target.value })}
                            placeholder={t("serviceTemplates.form.optionName")}
                            className="h-7 flex-1 text-sm"
                          />
                          <Input
                            type="number"
                            value={opt.durationModifier}
                            onChange={(e) =>
                              updateOption(gi, oi, {
                                durationModifier: parseInt(e.target.value, 10) || 0,
                              })
                            }
                            placeholder="0"
                            title={t("serviceTemplates.form.durationModifier")}
                            className="h-7 w-16 text-sm"
                          />
                          <div className="flex items-center gap-1">
                            <Checkbox
                              id={`default-${gi}-${oi}`}
                              checked={opt.isDefault}
                              onCheckedChange={(checked) =>
                                updateOption(gi, oi, { isDefault: checked === true })
                              }
                            />
                            <Label htmlFor={`default-${gi}-${oi}`} className="text-xs">
                              {t("serviceTemplates.form.isDefault")}
                            </Label>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive"
                            onClick={() => removeOption(gi, oi)}
                            disabled={group.options.length === 1}
                            title={t("serviceTemplates.form.removeOption")}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive mt-0"
                    onClick={() => removeGroup(gi)}
                    disabled={form.groups.length === 1}
                    title={t("serviceTemplates.form.removeGroup")}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                {gi < form.groups.length - 1 && <Separator />}
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={creating}>
            {t("actions.cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || creating}>
            {creating ? t("actions.saving") : t("actions.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
